/**
 * curatedOverlay — merge the brand's hand-curated presentation onto the live
 * Square menu.
 *
 * Square is the source of truth for what's orderable: prices, variations,
 * modifiers, images, and sold-out state. But the website's menu-data.json
 * carries personality Square doesn't have — richer descriptions, emoji/badges,
 * section storytelling, and combo cards. This module indexes that curated data
 * by a normalized item name and overlays it onto matching Square items, so the
 * screen shows Square's accuracy with Desi Bites' polish.
 *
 * All functions are pure (no React, no Firebase) so they're easy to unit test.
 */

/** Strip size/qty/parenthetical noise → a comparable core key. Mirrors squareNames. */
export function normalizeName(s) {
  return (s || '')
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .replace(/×\s*\d+/g, ' ')
    .replace(/\b\d+\s*(oz|pc|pcs|pieces?|ct|pack)\b/g, ' ')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Walk the curated menu-data.json and build  normalizedName → overlay  map.
 * Overlay fields: { desc, img, badge, emoji, sectionTitle }.
 */
export function buildOverlayIndex(menuData) {
  const index = {};
  if (!menuData || typeof menuData !== 'object') return index;

  const put = (name, overlay, sectionTitle) => {
    const key = normalizeName(name);
    if (!key) return;
    const clean = {};
    if (overlay.desc) clean.desc = overlay.desc;
    if (overlay.img) clean.img = overlay.img;
    if (overlay.badge) clean.badge = overlay.badge;
    if (overlay.icon) clean.emoji = overlay.icon;
    if (sectionTitle) clean.sectionTitle = sectionTitle;
    // First match wins (don't let a later generic entry clobber a specific one).
    if (!index[key]) index[key] = clean;
  };

  // sections: [{ title, columns: [[ {name,desc,img,price}, ... ], ... ] }]
  (menuData.sections || []).forEach(sec => {
    (sec.columns || []).forEach(col => {
      (col || []).forEach(it => it?.name && put(it.name, it, sec.title));
    });
    // A price matrix stores a style per row and a base per column; the orderable
    // name is the two joined, and the base's note is its description.
    (sec.rows || []).forEach(([style]) => {
      if (!style) return;
      (sec.priceColumns || []).forEach(base => {
        const name = style === 'Plain' ? base : `${style} ${base}`;
        put(name, { desc: sec.columnNotes?.[base] }, sec.title);
      });
    });
  });

  // specials: [ {name,desc,...} ]
  (menuData.specials || []).forEach(it => it?.name && put(it.name, it, 'Specials'));

  return index;
}

/**
 * Overlay curated fields onto each Square item (returns a new menu object).
 * Square fields always win for price/availability; curated only fills
 * description/image/emoji when Square is missing them or to add flavor.
 */
export function applyOverlay(squareMenu, index) {
  if (!squareMenu?.categories) return squareMenu;
  const idx = index || {};
  const categories = squareMenu.categories.map(cat => ({
    ...cat,
    items: cat.items.map(item => {
      const ov = idx[normalizeName(item.name)] || null;
      if (!ov) return { ...item, curated: null };
      return {
        ...item,
        // Square image wins; fall back to curated image if Square has none.
        imageUrl: item.imageUrl || ov.img || null,
        // Prefer the curated, more evocative description when present.
        description: ov.desc || item.description || '',
        emoji: ov.emoji || null,
        badge: ov.badge || null,
        curated: ov,
      };
    }),
  }));
  return { ...squareMenu, categories };
}

/** Convenience: index + apply in one call. */
export function mergeCuratedMenu(squareMenu, menuData) {
  return applyOverlay(squareMenu, buildOverlayIndex(menuData));
}
